import React, { FC, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, CardDeck, Card, CardBody, CardLink } from 'reactstrap';
import classnames from 'classnames';
import Link from 'next/link';
import TruncateMarkup from 'react-truncate-markup';
import parse from 'html-react-parser';
import InfiniteScroll from 'react-infinite-scroll-component';
import styles from './ClientList.module.scss';
import getBTURL from '@/libs/getBTURL/getBTURL';
import { fetcher } from '@/libs/fetcher/fetcher';
import useSWRInfinite from 'swr/infinite';
import OrgIdSkeleton from '@/components/Grids/ClientList/Skeleton';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
import { ClientData, clientOrgIdProps } from '@/libs/interfaceProps/clientListProps';

//import components
import GridImages from '@/components/GridImages/GridImages';
import { useLangContext } from '@/context/langContext';
import { useEnvContext } from '@/context/envContext';
import useResFallbackImages from '@/libs/resFallbackImages/resFallbackImages';
import { sitenameImageIndexHandler } from '@/libs/sitenameImageIndexHandler/sitenameImageIndexHandler';

// Libraries
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import setFallbackCatIndex from '@/libs/fallbackCatIndexCookies/setFallbackCatIndex';
const defaultSize = 9;

// Safe HTML parsing utility
const safeHtmlParse = (content: string | undefined) => {
  if (!content || typeof content !== 'string') return '';
  try {
    return parse(sanitizeTitle(content));
  } catch (e) {
    console.error('Error parsing HTML:', e);
    return content;
  }
};
const ClientList: FC<clientOrgIdProps> = ({
  orgId,
  category = null,
  rootUrl,
  peId,
  utmParams
}) => {
  const {
    lang
  } = useLangContext();
  const fallbackimages = useResFallbackImages();
  const {
    webUrl
  } = useEnvContext();

  // Global image index tracker - make it ref to avoid re-renders
  const sitenameImageIndex = React.useRef<Record<string, number>>({}).current;
  const getKey = useCallback((pageIndex: number) => {
    const start = pageIndex === 0 ? 0 : pageIndex * defaultSize;
    return `/hub/services/orgList?lang=${lang}&orgId=${orgId}&start=${start}&category=${category}&size=${defaultSize}`;
  }, [lang, orgId, category]);
  const {
    data,
    error,
    size,
    setSize,
    mutate
  } = useSWRInfinite(getKey, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  // Clear SWR cache on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // For useSWRInfinite, we need to clear the cache differently
      // Simply call mutate without arguments to clear this specific hook's cache
      mutate();
    };
  }, [mutate]);
  const getAssetURL = useCallback((resId: string, docType: string) => {
    if (docType === 'Webinar' || resId.includes('-')) {
      return `https://${getBTURL(webUrl)}/webcast/${resId.replace('-', '/')}${utmParams}`;
    } else {
      return `${rootUrl}/asset/${resId}/${peId}${utmParams}`;
    }
  }, [webUrl, rootUrl, peId, utmParams]);
  const {
    clientData,
    totalCount,
    foundCount
  } = useMemo(() => {
    const orgData: ClientData[] = [];
    let found = 0;
    if (data && data[0]?.found !== 0) {
      data.forEach((singleArray: any, index: number) => {
        if (!singleArray?.error) {
          if (index === 0) found = singleArray.found;
          if (Array.isArray(singleArray?.body)) {
            orgData.push(...singleArray.body);
          }
        }
      });
    }
    return {
      clientData: orgData,
      totalCount: orgData.length,
      foundCount: found
    };
  }, [data]);
  const isReachedEnd = totalCount >= foundCount;
  const loadMoreItems = useCallback(() => {
    if (!isReachedEnd) {
      setSize(size + 1);
    }
  }, [isReachedEnd, setSize, size]);

  // MOVE THIS HOOK BEFORE ANY CONDITIONAL RETURNS
  const renderCard = useCallback(({
    id,
    bestTitle,
    docType,
    shortDescription,
    resId,
    primarySiteName,
    thumbnail
  }: {
    id: any;
    bestTitle: any;
    docType: any;
    shortDescription: any;
    resId: any;
    primarySiteName?: any;
    thumbnail?: any;
  }, index: number) => {
    const siteName = primarySiteName || 'genericMulti';
    const resFallbackImageUrl = sitenameImageIndexHandler(sitenameImageIndex, fallbackimages, siteName);
    return <Col sm="12" md="6" lg="4" key={`clientlistitem-${id}-${index}`}>
     <CardDeck>
      <Card className={styles['clientlist-card']} data-category-name={siteName}>
       <Link className={classnames('stretched-link', styles['link-img-hover'])} href={getAssetURL(resId, docType)} onClick={() => {
            setFallbackCatIndex([{
              resId: resId,
              url: resFallbackImageUrl
            }]);
            pushGTMClickTags({
              click_element: 'Anchor',
              click_closest_class: 'clientlist-card',
              click_section_ID: 'NONE',
              link_classes: 'link-img-hover',
              link_id: 'NONE',
              link_text: 'NONE',
              link_url: `/hub/asset/${resId}`,
              click_category: siteName,
              click_destination: 'asset-page',
              click_closest_href: 'NONE'
            });
          }} prefetch={false} target={docType === 'Webinar' || resId.includes('-') ? '_blank' : '_self'}></Link>
       <div className={classnames(styles['card-image-wrap'], 'faux-letterbox')}>
        {docType === 'Webinar' ? <GridImages GridType={'webinar'} resId={resId} thumbnail={thumbnail} primarySiteName={siteName} fallbackimage={resFallbackImageUrl} /> : <GridImages GridType={'clientlist'} resId={resId} primarySiteName={siteName} fallbackimage={resFallbackImageUrl} />}
       </div>
       <CardBody className={styles['clientlist-body']}>
        {typeof bestTitle === 'string' && bestTitle?.length > 0 && <TruncateMarkup lines={2} tokenize={'words'}>
          <h2 className={styles['clientlist-title']}>
           {safeHtmlParse(bestTitle)}
          </h2>
         </TruncateMarkup>}
        {typeof shortDescription === 'string' && shortDescription?.length > 0 && <TruncateMarkup lines={3} tokenize={'words'}>
           <p>{safeHtmlParse(shortDescription)}</p>
          </TruncateMarkup>}
        <CardLink className={styles['clientlist-tag']} href="#">
         {docType}
        </CardLink>
       </CardBody>
      </Card>
     </CardDeck>
    </Col>;
  }, [fallbackimages, getAssetURL, sitenameImageIndex]);

  // NOW place conditional returns AFTER all hooks are defined
  if (error) return null;
  if (!error && !data) return <OrgIdSkeleton />;
  return <InfiniteScroll className={'infinite-scroll'} dataLength={totalCount} next={loadMoreItems} hasMore={!isReachedEnd} loader={<OrgIdSkeleton />} scrollThreshold={0.8} data-sentry-element="InfiniteScroll" data-sentry-component="ClientList" data-sentry-source-file="ClientList.tsx">
   <Row className={classnames(totalCount >= foundCount ? styles['clientlist-row'] : '', 'g-3')} data-bdd="clientlist-row" data-sentry-element="Row" data-sentry-source-file="ClientList.tsx">
    {clientData.map((item, index) => renderCard(item, index))}
   </Row>
  </InfiniteScroll>;
};
export default React.memo(ClientList);